import { clean } from "@/lib/resumeRender/model";
import type { ContactItem, RenderEntry, RenderResume, RenderSection, RichLine } from "@/lib/resumeRender/model";
import {
  customSection,
  formatDate,
  formatRange,
  isEmptyCertification,
  isEmptyEducation,
  isEmptyExperience,
  isEmptyItem,
  isEmptyProject,
  sectionTitle,
  visibleSections,
} from "./model";
import {
  LANGUAGE_LEVEL_LABELS,
  LANGUAGE_LEVELS,
  SKILL_LEVEL_LABELS,
  SKILL_LEVELS,
  defaultSectionIconId,
  levelFraction,
  schemaOf,
  type CustomSection,
  type DateFormat,
  type Resume,
} from "./types";

// Port of the app's render_model.dart `buildRenderModel`: the
// template-independent, print-ready view of a resume — hidden/empty sections
// removed, dates formatted, free text split into paragraphs and bullets.

const BULLET_PREFIX = /^\s*([-•*–·▪●◦]|\d+[.)])\s+/;

/**
 * Splits free text into paragraphs and bullets: lines starting with "-",
 * "•", "*", "–" or "1." become bullets; consecutive plain lines join into one
 * paragraph.
 */
export function parseRichText(raw: string | null | undefined): RichLine[] {
  const out: RichLine[] = [];
  let paragraph = "";
  const flush = () => {
    const p = clean(paragraph);
    if (p) out.push({ text: p, bullet: false });
    paragraph = "";
  };
  for (const line of (raw ?? "").replace(/\r\n/g, "\n").split("\n")) {
    if (!line.trim()) {
      flush();
      continue;
    }
    if (BULLET_PREFIX.test(line)) {
      flush();
      const b = clean(line.replace(BULLET_PREFIX, ""));
      if (b) out.push({ text: b, bullet: true });
    } else {
      paragraph += (paragraph ? " " : "") + line.trim();
    }
  }
  flush();
  return out;
}

const join = (parts: (string | null | undefined)[], sep = " · ") =>
  parts
    .map((p) => clean(p))
    .filter(Boolean)
    .join(sep);

/** A clickable URL for a typed link ("linkedin.com/in/x" → "https://…"). */
export function normalizeUrl(url: string): string {
  const u = url.trim();
  if (!u) return u;
  if (u.includes("@") && !u.includes("/")) return `mailto:${u}`;
  return /^[a-z]+:/i.test(u) ? u : `https://${u}`;
}

/** "linkedin.com/in/jane" for display: no scheme, no www, no trailing slash. */
export function displayUrl(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/$/, "");
}

export type BuildOptions = {
  /** Live previews show "Your Name" while the name is still empty. */
  placeholders?: boolean;
};

export const PLACEHOLDER_NAME = "Your Name";

export function buildRenderModel(r: Resume, o: BuildOptions = {}): RenderResume {
  const d = r.personalDetails;
  const pattern = r.design.dateFormat;
  const contacts: ContactItem[] = [];
  const add = (kind: ContactItem["kind"], text: string, url?: string) => contacts.push(url ? { kind, text, url } : { kind, text });

  const email = clean(d.email);
  if (email) add("email", email, `mailto:${email}`);
  const phone = clean(d.phone);
  if (phone) add("phone", phone, `tel:${d.phone.replace(/[^\d+]/g, "")}`);
  const location = clean(d.location);
  if (location) add("location", location);
  for (const [kind, value] of [
    ["linkedin", d.linkedIn],
    ["github", d.github],
    ["website", d.website],
  ] as const) {
    const v = clean(value);
    if (v) add(kind, displayUrl(v), normalizeUrl(v));
  }
  for (const link of d.links) {
    const url = clean(link.url);
    if (url) add("link", clean(link.label) || displayUrl(url), normalizeUrl(url));
  }
  const birth = clean(d.dateOfBirth);
  if (birth) add("birth", birth);
  const nationality = clean(d.nationality);
  if (nationality) add("nationality", nationality);

  const sections: RenderSection[] = [];
  for (const key of visibleSections(r)) {
    const title = clean(sectionTitle(r, key));
    const base = { key, title, entries: [] as RenderEntry[], skills: [], lines: [], tags: [] };
    switch (key) {
      case "summary":
        sections.push({ ...base, type: "summary", lines: parseRichText(d.summary) });
        break;
      case "experience":
        sections.push({
          ...base,
          type: "experience",
          entries: r.experience
            .filter((e) => !isEmptyExperience(e))
            .map((e) => ({
              title: clean(e.jobTitle),
              subtitle: clean(e.company),
              meta: join([e.location, e.employmentType]),
              dates: formatRange(e.startDate, e.endDate, e.isCurrent, pattern),
              lines: [
                ...parseRichText(e.description),
                ...e.bulletPoints
                  .map((b) => clean(b.replace(BULLET_PREFIX, "")))
                  .filter(Boolean)
                  .map((text) => ({ text, bullet: true })),
              ],
              tags: [],
            })),
        });
        break;
      case "education":
        sections.push({
          ...base,
          type: "education",
          entries: r.education
            .filter((e) => !isEmptyEducation(e))
            .map((e) => ({
              title: join([e.degree, e.fieldOfStudy], ", "),
              subtitle: clean(e.institution),
              meta: join([e.location, e.grade]),
              dates: formatRange(e.startDate, e.endDate, e.isCurrent, pattern),
              lines: parseRichText(e.description),
              tags: [],
            })),
        });
        break;
      case "skills":
        sections.push({
          ...base,
          type: "skills",
          skills: r.skills
            .filter((s) => clean(s.name))
            .map((s) => ({
              name: clean(s.name),
              level: levelFraction(SKILL_LEVELS, s.level),
              levelLabel: SKILL_LEVEL_LABELS[s.level],
              category: clean(s.category),
            })),
        });
        break;
      case "projects":
        sections.push({
          ...base,
          type: "projects",
          entries: r.projects
            .filter((p) => !isEmptyProject(p))
            .map((p) => {
              const link = clean(p.link);
              return {
                title: clean(p.name),
                subtitle: clean(p.role),
                meta: link ? displayUrl(link) : "",
                url: link ? normalizeUrl(link) : undefined,
                dates: formatRange(p.startDate, p.endDate, p.isCurrent, pattern),
                lines: parseRichText(p.description),
                tags: p.technologies.map((t) => clean(t)).filter(Boolean),
              };
            }),
        });
        break;
      case "certifications":
        sections.push({
          ...base,
          type: "certifications",
          entries: r.certifications
            .filter((c) => !isEmptyCertification(c))
            .map((c) => {
              const url = clean(c.credentialUrl);
              return {
                title: clean(c.name),
                subtitle: clean(c.issuer),
                meta: clean(c.credentialId) ? `ID ${clean(c.credentialId)}` : "",
                dates: formatDate(c.issueDate, pattern),
                url: url ? normalizeUrl(url) : undefined,
                lines: [],
                tags: [],
              };
            }),
        });
        break;
      case "languages":
        sections.push({
          ...base,
          type: "languages",
          skills: r.languages
            .filter((l) => clean(l.name))
            .map((l) => ({
              name: clean(l.name),
              level: levelFraction(LANGUAGE_LEVELS, l.proficiency),
              levelLabel: LANGUAGE_LEVEL_LABELS[l.proficiency],
              category: "",
            })),
        });
        break;
      default: {
        const custom = customSection(r, key);
        if (custom) sections.push(customRenderSection(custom, title, pattern));
      }
    }
  }

  const name = clean(d.fullName);
  return {
    name: name || (o.placeholders ? PLACEHOLDER_NAME : ""),
    headline: clean(d.headline),
    contacts,
    sections: sections.map((s) => ({
      ...s,
      iconId: r.design.sectionIconChoices[s.key] ?? defaultSectionIconId(s.key, customSection(r, s.key)?.kind),
    })),
    photo: r.design.showPhoto ? d.photo : null,
  };
}

function customRenderSection(s: CustomSection, title: string, pattern: DateFormat): RenderSection {
  const schema = schemaOf(s.kind);
  const items = s.items.filter((i) => !isEmptyItem(i));
  const base = { key: s.id, title, entries: [] as RenderEntry[], skills: [], lines: [] as RichLine[], tags: [] as string[] };
  switch (schema.layout) {
    case "tags":
      return { ...base, type: "tags", tags: items.map((i) => clean(i.heading)).filter(Boolean) };
    case "bullets":
      return { ...base, type: "bullets", lines: items.map((i) => ({ text: join([i.heading, i.description], " — "), bullet: true })) };
    case "references":
      return {
        ...base,
        type: "references",
        entries: items.map((i) => ({
          title: clean(i.heading),
          subtitle: clean(i.subheading),
          meta: clean(i.description),
          dates: "",
          lines: [],
          tags: [],
        })),
      };
    default:
      return {
        ...base,
        type: "entries",
        entries: items.map((i) => {
          const url = clean(i.url);
          return {
            title: clean(i.heading),
            subtitle: clean(i.subheading),
            meta: join([schema.locationLabel ? i.location : "", schema.urlLabel && url ? displayUrl(url) : ""]),
            url: url ? normalizeUrl(url) : undefined,
            dates:
              schema.dateMode === "single"
                ? formatDate(i.date ?? i.endDate, pattern)
                : schema.dateMode === "range"
                  ? formatRange(i.date, i.endDate, i.isCurrent, pattern)
                  : "",
            lines: parseRichText(i.description),
            tags: [],
          };
        }),
      };
  }
}
